/**
 * OnboardingTour.tsx
 *
 * Shepherd.js guided product tour for CBOps.
 *
 * Features (Session 21 polish):
 *   - 8 targeted steps with data-tour attribute selectors
 *   - Step progress counter injected into each tooltip header ("Step 2 of 8")
 *   - Glassmorphism styling matching the CBOps design system
 *   - Skip Tour button on the welcome step
 *   - Finish button on the last step
 *   - Completion + cancellation both write pulsehq_tour_seen = 'true' to localStorage
 *   - Guard against double-start if a tour is already active
 *   - Conditional attachment: falls back to centred modal if the target element
 *     is not found in the DOM (e.g. mobile sidebar hidden)
 *
 * data-tour attributes used:
 *   [data-tour="sidebar"]          — the <aside> navigation element
 *   [data-tour="topbar-search"]    — search bar in Topbar
 *   [data-tour="notification-bell"]— bell icon in Topbar
 *   [data-tour="theme-switcher"]   — floating gear button
 *   [data-tour="help-button"]      — floating ? help button
 *   [data-tour="stat-grid"]        — dashboard stat cards container
 *   [data-tour="setup-checklist"]  — "Getting Started" sidebar link
 */

import { useEffect, useCallback } from 'react';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';

// ── Constants ─────────────────────────────────────────────────────────────────

const TOUR_SEEN_KEY = 'pulsehq_tour_seen';
const TOTAL_STEPS   = 8;

// ── Glass CSS overrides ───────────────────────────────────────────────────────

function injectShepherdStyles(): void {
  if (document.getElementById('shepherd-glass-styles')) return;

  const style = document.createElement('style');
  style.id = 'shepherd-glass-styles';
  style.textContent = `
    .shepherd-element {
      background: rgba(10, 18, 36, 0.96) !important;
      border: 1px solid rgba(255,255,255,0.15) !important;
      border-radius: 14px !important;
      color: white !important;
      max-width: 320px !important;
      box-shadow: 0 24px 48px rgba(0,0,0,0.5) !important;
    }
    .shepherd-header {
      background: transparent !important;
      padding: 14px 16px 4px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;
    }
    .shepherd-title {
      color: white !important;
      font-weight: 600 !important;
      font-size: 15px !important;
      flex: 1 !important;
    }
    .shepherd-has-title .shepherd-content .shepherd-header {
      background: transparent !important;
    }
    .shepherd-text {
      color: rgba(255,255,255,0.75) !important;
      font-size: 13px !important;
      line-height: 1.65 !important;
      padding: 4px 16px 14px !important;
    }
    .shepherd-footer {
      border-top: 1px solid rgba(255,255,255,0.08) !important;
      padding: 10px 14px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;
      gap: 8px !important;
    }
    .shepherd-button {
      border-radius: 8px !important;
      font-size: 12px !important;
      font-weight: 500 !important;
      padding: 6px 14px !important;
      cursor: pointer !important;
      border: none !important;
      transition: opacity 0.15s ease !important;
    }
    .shepherd-button:hover { opacity: 0.85 !important; }
    .shepherd-button-primary {
      background: #14b8a6 !important;
      color: white !important;
    }
    .shepherd-button-secondary {
      background: rgba(255,255,255,0.08) !important;
      color: rgba(255,255,255,0.7) !important;
    }
    .shepherd-arrow::before {
      background: rgba(10,18,36,0.96) !important;
      border-color: rgba(255,255,255,0.15) !important;
    }
    .shepherd-cancel-icon {
      color: rgba(255,255,255,0.4) !important;
      font-size: 20px !important;
    }
    .shepherd-cancel-icon:hover { color: rgba(255,255,255,0.9) !important; }
    /* Step counter badge injected into header */
    .shepherd-step-counter {
      font-size: 11px;
      color: rgba(255,255,255,0.35);
      font-weight: 400;
      margin-left: 8px;
      white-space: nowrap;
    }
    /* Progress dots row */
    .shepherd-progress-dots {
      display: flex;
      gap: 5px;
      align-items: center;
      padding: 0 2px;
    }
    .shepherd-dot {
      width: 5px; height: 5px;
      border-radius: 50%;
      background: rgba(255,255,255,0.18);
      transition: background 0.2s ease, transform 0.2s ease;
    }
    .shepherd-dot.active {
      background: #14b8a6;
      transform: scale(1.3);
    }
  `;
  document.head.appendChild(style);
}

// ── Step definitions ──────────────────────────────────────────────────────────

interface StepDef {
  id: string;
  title: string;
  text: string;
  attachSelector?: string;
  attachOn?: 'top' | 'bottom' | 'left' | 'right';
}

const STEP_DEFS: StepDef[] = [
  {
    id:    'welcome',
    title: 'Welcome to CBOps 👋',
    text:  "Let's take a quick tour — 8 steps, under 90 seconds. You can skip at any time.",
  },
  {
    id:            'sidebar',
    title:         'Navigation Sidebar',
    text:          'Everything is organised here — HR, Performance, Work, CRM, Finance, Support, and more. Click any section to expand it.',
    attachSelector: '[data-tour="sidebar"]',
    attachOn:       'right',
  },
  {
    id:            'stat-grid',
    title:         'Live Dashboard Stats',
    text:          'These cards pull live counts directly from your database — employees, KPIs, open tickets, and more. They update in real time.',
    attachSelector: '[data-tour="stat-grid"]',
    attachOn:       'bottom',
  },
  {
    id:            'notification-bell',
    title:         'Real-time Notifications',
    text:          'The bell lights up instantly when leave is approved, a task is assigned to you, or a ticket is replied to — powered by Supabase Realtime.',
    attachSelector: '[data-tour="notification-bell"]',
    attachOn:       'bottom',
  },
  {
    id:            'topbar-search',
    title:         'Quick Search',
    text:          'Search across the app here, or press Ctrl+K (⌘K on Mac) to open the command palette — navigate any page without the mouse.',
    attachSelector: '[data-tour="topbar-search"]',
    attachOn:       'bottom',
  },
  {
    id:            'setup-checklist',
    title:         'Getting Started Checklist',
    text:          'Follow this checklist to fully configure your workspace — departments, employees, leave policies, integrations, and more.',
    attachSelector: '[data-tour="setup-checklist"]',
    attachOn:       'right',
  },
  {
    id:            'help-button',
    title:         'Help & Shortcuts',
    text:          'This button is always here — page guides, keyboard shortcuts cheatsheet, and replay this tour whenever you like.',
    attachSelector: '[data-tour="help-button"]',
    attachOn:       'left',
  },
];

// ── Progress dots builder ─────────────────────────────────────────────────────

function buildProgressDots(currentIdx: number): string {
  return `<div class="shepherd-progress-dots">
    ${STEP_DEFS.map((_, i) =>
      `<div class="shepherd-dot${i === currentIdx ? ' active' : ''}"></div>`
    ).join('')}
  </div>`;
}

// ── useOnboardingTour hook ────────────────────────────────────────────────────

export function useOnboardingTour() {
  const hasSeenTour = localStorage.getItem(TOUR_SEEN_KEY) === 'true';

  const startTour = useCallback((onComplete?: () => void) => {
    injectShepherdStyles();

    const tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        cancelIcon: { enabled: true },
        scrollTo: { behavior: 'smooth', block: 'center' },
      },
    });

    STEP_DEFS.forEach((def, idx) => {
      const isFirst = idx === 0;
      const isLast  = idx === STEP_DEFS.length - 1;

      // Resolve attachment — skip if target not in DOM
      const el = def.attachSelector ? document.querySelector(def.attachSelector) : null;
      const attachTo = (el && def.attachSelector)
        ? { element: def.attachSelector, on: def.attachOn ?? 'bottom' as const }
        : undefined;

      const buttons = [];

      if (isFirst) {
        buttons.push({
          text: 'Skip Tour',
          action() { tour.cancel(); },
          classes: 'shepherd-button-secondary',
        });
        buttons.push({
          text: 'Start Tour →',
          action() { tour.next(); },
          classes: 'shepherd-button-primary',
        });
      } else if (isLast) {
        buttons.push({
          text: '← Back',
          action() { tour.back(); },
          classes: 'shepherd-button-secondary',
        });
        buttons.push({
          text: 'Finish ✓',
          action() { tour.complete(); },
          classes: 'shepherd-button-primary',
        });
      } else {
        buttons.push({
          text: '← Back',
          action() { tour.back(); },
          classes: 'shepherd-button-secondary',
        });
        buttons.push({
          text: 'Next →',
          action() { tour.next(); },
          classes: 'shepherd-button-primary',
        });
      }

      tour.addStep({
        id:       def.id,
        title:    `${def.title} <span class="shepherd-step-counter">${idx + 1} / ${TOTAL_STEPS}</span>`,
        text:     `${def.text}<br/><br/>${buildProgressDots(idx)}`,
        attachTo,
        buttons,
      });
    });

    tour.on('complete', () => {
      localStorage.setItem(TOUR_SEEN_KEY, 'true');
      onComplete?.();
    });

    tour.on('cancel', () => {
      localStorage.setItem(TOUR_SEEN_KEY, 'true');
      onComplete?.();
    });

    tour.start();
  }, []);

  return { startTour, hasSeenTour };
}

// ── Component ─────────────────────────────────────────────────────────────────

interface OnboardingTourProps {
  run: boolean;
  onComplete: () => void;
}

export default function OnboardingTour({ run, onComplete }: OnboardingTourProps) {
  const { startTour } = useOnboardingTour();

  useEffect(() => {
    if (!run) return;
    if (Shepherd.activeTour) return;
    startTour(onComplete);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run]);

  return null;
}
